import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TopBar from "@/components/TopBar";
import HorizonRing from "@/components/HorizonRing";
import ShareModal from "@/components/ShareModal";
import { BRANCH_META, BRANCH_ORDER } from "@/lib/branches";
import { Award, Download, Flame, ScrollText, Share2, Crown, Gift, Copy, Check, Users } from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
    const [data, setData] = useState(null);
    const [showShare, setShowShare] = useState(false);
    const [referrals, setReferrals] = useState(null);
    const [copied, setCopied] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const { user: authUser } = useAuth();

    const load = () => {
        setLoadError(null);
        api.get("/profile/me")
            .then((r) => setData(r.data))
            .catch((e) => {
                // /profile/me is the ONLY blocker; if it dies, we still have
                // the user identity from AuthContext + a "try again" nudge.
                // Fall back to a graceful degraded view instead of a blank page.
                setLoadError(
                    e?.response?.data?.detail ||
                    "We couldn't load your full ledger. Check your connection and retry."
                );
            });
        api.get("/referrals/me").then((r) => setReferrals(r.data)).catch(() => {});
    };

    useEffect(() => {
        load();
    }, []);

    const inviteUrl = referrals?.code
        ? `${window.location.origin}/?ref=${referrals.code}`
        : "";

    const copyInvite = async () => {
        if (!inviteUrl) return;
        try {
            await navigator.clipboard.writeText(inviteUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch {}
    };

    // Graceful degradation: if /profile/me fails, still render the page using
    // the identity from AuthContext + empty beacons/ledger/quests. The user
    // sees their identity card, XP bars, resume link, and referral tools —
    // instead of a blank error page. A small inline banner offers a retry.
    const effective = data || (authUser
        ? { user: authUser, beacons: [], ledger: [], quests: [] }
        : null);

    if (!effective) {
        return (
            <div className="min-h-screen w-full">
                <TopBar />
                <div className="max-w-4xl mx-auto pt-24 pb-16 px-4 sm:px-8 text-center text-slate-500 font-display">
                    Loading ledger…
                </div>
            </div>
        );
    }

    const { user, beacons, ledger, quests } = effective;
    const initials = (user.name || "?")
        .split(" ")
        .map((s) => s[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();

    // Determine master status from server-computed master_branches (>=300 XP in a branch)
    const masters = user.master_branches || [];
    const primaryBranch = masters[0] || Object.entries(user.branch_xp || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || "sciences";
    const meta = BRANCH_META[primaryBranch];

    return (
        <div className="min-h-screen pt-24 pb-16 px-4 sm:px-8">
            <TopBar />

            <div className="max-w-5xl mx-auto">
                {/* Degraded-mode notice: we're rendering with AuthContext data
                    because /profile/me failed. User can still see their
                    identity + resume; the retry recovers the ledger detail. */}
                {loadError && !data && (
                    <div
                        className="mb-4 crystal rounded-3xl p-4 flex flex-wrap items-start gap-3"
                        data-testid="profile-degraded-notice"
                    >
                        <div className="min-w-0 flex-1">
                            <div className="font-display font-bold text-slate-900 text-sm">
                                Some ledger details couldn't load
                            </div>
                            <div className="text-xs text-slate-600 mt-0.5">{loadError}</div>
                        </div>
                        <button
                            onClick={load}
                            className="arcade-btn"
                            data-variant="primary"
                            data-testid="profile-load-retry"
                        >
                            Retry
                        </button>
                    </div>
                )}
                {/* Header */}
                <div
                    className="crystal rounded-3xl p-8 relative overflow-hidden"
                    style={{
                        background: `radial-gradient(circle at 90% 10%, ${meta.soft} 0%, #fff 60%)`,
                    }}
                >
                    <div className="flex flex-col sm:flex-row items-start gap-6">
                        <div
                            className="w-24 h-24 rounded-2xl flex items-center justify-center font-display font-black text-4xl text-white shadow-lg relative"
                            style={{
                                background: `linear-gradient(135deg, ${meta.color}, ${BRANCH_META.humanities.color})`,
                                boxShadow: `0 12px 40px -8px ${meta.color}`,
                            }}
                            data-testid="profile-avatar"
                        >
                            {initials}
                            {masters.length > 0 && (
                                <Crown
                                    className="absolute -top-2 -right-2 w-8 h-8 rotate-12 drop-shadow-lg"
                                    color="#FFC107"
                                    fill="#FFC107"
                                    data-testid="profile-master-crown"
                                />
                            )}
                        </div>
                        <div className="flex-1">
                            <div className="chip" style={{ background: meta.soft, color: meta.text }}>
                                {masters.length > 0 ? (
                                    <>
                                        <Crown className="w-3 h-3" />
                                        Discipline Master · {masters.map((m) => BRANCH_META[m]?.name).join(" · ")}
                                    </>
                                ) : (
                                    <>Explorer · Level {user.level}</>
                                )}
                            </div>
                            <h1
                                className="font-display font-black text-5xl tracking-tighter text-slate-900 mt-2 leading-none"
                                data-testid="profile-name"
                            >
                                {user.name}
                            </h1>
                            <p className="text-slate-500 font-mono text-sm mt-1">{user.email}</p>
                            <div className="text-sm text-slate-600 mt-3">
                                <span className="font-display font-bold" data-testid="profile-xp">{user.xp}</span> XP · {beacons.length}{" "}
                                beacon{beacons.length !== 1 ? "s" : ""} · {ledger.length} contributions
                            </div>
                        </div>
                        <button className="arcade-btn" data-variant="humanities" data-testid="share-profile-btn" onClick={() => setShowShare(true)}>
                            <Share2 className="w-4 h-4" /> Share ledger
                        </button>
                        <a
                            href={`${BACKEND_URL}/api/profile/resume.pdf`}
                            className="arcade-btn"
                            data-variant="ghost"
                            data-testid="download-resume"
                        >
                            <Download className="w-4 h-4" /> Export PDF
                        </a>
                    </div>

                    {/* Branch XP */}
                    <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {BRANCH_ORDER.map((b) => {
                            const xp = (user.branch_xp || {})[b] || 0;
                            const m = BRANCH_META[b];
                            return (
                                <div
                                    key={b}
                                    className="rounded-2xl p-3 flex items-center gap-3"
                                    style={{ background: m.soft }}
                                    data-testid={`branch-xp-${b}`}
                                >
                                    <HorizonRing size={54} stroke={7} pct={Math.min(100, (xp / 250) * 100)} color={m.color} />
                                    <div>
                                        <div className="text-xs uppercase tracking-widest font-bold" style={{ color: m.text }}>
                                            {m.name}
                                        </div>
                                        <div className="font-display font-black text-xl tracking-tighter text-slate-900">
                                            {xp}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Invite friends — stackable free months */}
                <div className="mt-8 crystal rounded-3xl p-6" data-testid="invite-friends-section">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <Gift className="w-4 h-4" color={BRANCH_META.trades.color} />
                            <h2 className="font-display font-bold text-xl tracking-tight text-slate-900">
                                Invite friends — earn free months
                            </h2>
                        </div>
                        {referrals && (
                            <div className="flex items-center gap-3 text-sm">
                                <span className="chip bg-emerald-50 text-emerald-800" data-testid="referrals-count">
                                    <Users className="w-3 h-3" /> {referrals.referrals_made} invited
                                </span>
                                {referrals.days_of_credit > 0 && (
                                    <span className="chip bg-amber-50 text-amber-800" data-testid="referrals-credit">
                                        <Gift className="w-3 h-3" /> {referrals.days_of_credit} days of credit
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <p className="text-sm text-slate-600 mt-2">
                        Every friend who joins with your link gives BOTH of you a free month of Atlas. Stack them — 10 invites = 10 free months.
                    </p>
                    {referrals?.code && (
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                            <code className="font-mono text-sm px-3 py-2 rounded-xl bg-white/80 border border-slate-200 flex-1 min-w-[220px] break-all" data-testid="invite-url">
                                {inviteUrl}
                            </code>
                            <button
                                onClick={copyInvite}
                                className="arcade-btn"
                                data-variant="primary"
                                data-testid="copy-invite-btn"
                            >
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                {copied ? "Copied!" : "Copy invite link"}
                            </button>
                        </div>
                    )}
                    {referrals?.recent?.length > 0 && (
                        <div className="mt-4">
                            <div className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-2">
                                Recent joiners
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {referrals.recent.slice(0, 8).map((r, i) => (
                                    <Link
                                        key={i}
                                        to={`/u/${r.username}`}
                                        className="chip bg-white/70 text-slate-700 hover:bg-slate-900 hover:text-cream transition-all"
                                        data-testid={`referral-recent-${i}`}
                                    >
                                        {r.name}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Badges */}
                <div className="mt-8 crystal rounded-3xl p-6">
                    <div className="flex items-center gap-2">
                        <Award className="w-4 h-4" color={BRANCH_META.arts.color} />
                        <h2 className="font-display font-bold text-xl tracking-tight text-slate-900">
                            Merit badges
                        </h2>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {(user.badges || []).length === 0 && (
                            <p className="text-sm text-slate-500">
                                No badges yet — complete your first micro-quest to unlock one.
                            </p>
                        )}
                        {(user.badges || []).map((b, i) => {
                            const m = BRANCH_META[b.branch] || BRANCH_META.arts;
                            return (
                                <div
                                    key={i}
                                    className="chip"
                                    style={{ background: m.soft, color: m.text, border: `1.5px solid ${m.color}` }}
                                    data-testid={`badge-${i}`}
                                >
                                    <Award className="w-3 h-3" /> {b.name}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Beacons */}
                <div className="mt-6 grid md:grid-cols-2 gap-6">
                    <div className="crystal rounded-3xl p-6">
                        <div className="flex items-center gap-2">
                            <Flame className="w-4 h-4" color={BRANCH_META.sciences.color} />
                            <h2 className="font-display font-bold text-xl tracking-tight text-slate-900">
                                Ignited beacons
                            </h2>
                        </div>
                        <div className="mt-3 space-y-2">
                            {beacons.length === 0 && (
                                <p className="text-sm text-slate-500">No beacons yet. Head to the Atlas and light one up.</p>
                            )}
                            {beacons.map((b, i) => (
                                <Link
                                    key={i}
                                    to={`/discipline/${b.discipline_slug}`}
                                    className="block rounded-xl p-3 hover:bg-white/70 transition-colors"
                                    data-testid={`beacon-${b.discipline_slug}`}
                                >
                                    <div className="font-display font-bold text-slate-900 text-sm">
                                        {b.discipline_slug}
                                    </div>
                                    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
                                        {b.mode} · {new Date(b.ignited_at).toLocaleDateString()}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                    <div className="crystal rounded-3xl p-6">
                        <div className="flex items-center gap-2">
                            <ScrollText className="w-4 h-4" color={BRANCH_META.humanities.color} />
                            <h2 className="font-display font-bold text-xl tracking-tight text-slate-900">
                                Living ledger
                            </h2>
                        </div>
                        <div className="mt-3 space-y-2 max-h-72 overflow-y-auto no-scrollbar">
                            {ledger.length === 0 && (
                                <p className="text-sm text-slate-500">Contributions will appear here.</p>
                            )}
                            {ledger.map((e, i) => (
                                <div key={i} className="ledger-row text-slate-700">
                                    <span className="text-humanities font-bold uppercase">
                                        {String(e.event_type).padEnd(8, " ")}
                                    </span>{" "}
                                    <span className="text-slate-400">
                                        {new Date(e.created_at).toLocaleDateString()}
                                    </span>{" "}
                                    <span>{e.message}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {quests.length > 0 && (
                    <div className="mt-6 crystal rounded-3xl p-6">
                        <h2 className="font-display font-bold text-xl tracking-tight text-slate-900">
                            Global quests joined
                        </h2>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {quests.map((q, i) => {
                                const m = BRANCH_META[q.branch];
                                return (
                                    <div
                                        key={i}
                                        className="chip"
                                        style={{ background: m.soft, color: m.text }}
                                    >
                                        {q.quest_slug} · {q.branch}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            <ShareModal open={showShare} onClose={() => setShowShare(false)} />
        </div>
    );
}
